/**
 * FieldReturnTab (M4) — MRN workflow: Site Engineer creates MRN (references
 * MIR + qty + return type + condition; damaged requires photos) → Pickup
 * scan #9 → auto Return Gate Pass → Site-Exit #10 → WH-Entry #11 → Receipt
 * Counter #12 → optional Damage Assessment → ledger posted.
 */
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import ScanCenterDialog from '@/components/mtx/ScanCenterDialog';
import GatePassLabel from '@/components/mtx/GatePassLabel';
import { PackageX, Plus, X, Check, ChevronRight, ScanLine, Loader2, Sparkles,
         Camera, AlertTriangle, RefreshCw, Printer } from 'lucide-react';

const C = { primary: '#1E4E8C', accent: '#F26B1F', slate: '#0F172A',
             slateMuted: '#64748B', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444', info: '#3B82F6' };

const RETURN_TYPES = ['UNUSED', 'SURPLUS', 'DAMAGED', 'WRONG_ISSUE', 'PRESERVATION'];
const SALVAGE = ['SALVAGEABLE', 'RE_INSPECT', 'SCRAP'];
const BUCKETS = [
  { key: 'DRAFTED',       label: 'Drafted',        color: '#94A3B8' },
  { key: 'APPROVED',      label: 'Approved',       color: '#3B82F6' },
  { key: 'PICKED_UP',     label: 'Picked Up',      color: '#8B5CF6' },
  { key: 'AT_SITE_EXIT',  label: 'At Site Exit',   color: '#F97316' },
  { key: 'IN_TRANSIT',    label: 'In Transit',     color: '#0EA5E9' },
  { key: 'AT_WH_ENTRY',   label: 'At WH Entry',    color: '#F26B1F' },
  { key: 'RECEIVED',      label: 'Received',       color: '#22C55E' },
  { key: 'RECONCILED',    label: 'Reconciled',     color: '#059669' },
];

function CreateMrnDialog({ projectId, onClose, onCreated }) {
  const [returnType, setReturnType] = useState('UNUSED');
  const [warehouseLocation, setWarehouseLocation] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [mirId, setMirId] = useState('');
  const [mirSearch, setMirSearch] = useState('');
  const [mirResults, setMirResults] = useState([]);
  const [mirPicked, setMirPicked] = useState(null);
  const [unitIdsInput, setUnitIdsInput] = useState('');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState('');
  const [conditionNotes, setConditionNotes] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [saving, setSaving] = useState(false);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('ciq_user') || '{}'); }
    catch { return {}; }
  })();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/warehouses`);
        setWarehouses(data.warehouses || []);
        if ((data.warehouses || []).length && !warehouseLocation) {
          setWarehouseLocation(data.warehouses[0].location);
        }
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // MIR search — debounced
  useEffect(() => {
    if (mirSearch.length < 2) { setMirResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/mirs?q=${encodeURIComponent(mirSearch)}&limit=15`);
        setMirResults((data.mirs || []).filter(m =>
          ['APPROVED', 'ISSUED', 'AT_WH_GATE', 'IN_TRANSIT', 'AT_SITE_GATE', 'RECEIVED', 'INSTALLED'].includes(m.status)));
      } catch { setMirResults([]); }
    }, 280);
    return () => clearTimeout(t);
  }, [projectId, mirSearch]);

  const pickMir = (m) => {
    setMirId(m.id); setMirPicked(m); setMirSearch(''); setMirResults([]);
    if (m.warehouseLocation && !warehouseLocation) setWarehouseLocation(m.warehouseLocation);
    // Autofill unit ids from MIR reserved units
    if (m.reservedUnitIds?.length) setUnitIdsInput(m.reservedUnitIds.join(', '));
  };

  const submit = async () => {
    if (!warehouseLocation.trim()) return toast.error('Warehouse required');
    const unitIds = unitIdsInput.split(',').map(s => s.trim()).filter(Boolean);
    if (returnType === 'DAMAGED' && !photos.trim())
      return toast.error('Damaged returns require at least one photo URL');
    setSaving(true);
    try {
      const body = { returnType, warehouseLocation: warehouseLocation.trim(),
        mirId: mirId.trim() || null, materialUnitIds: unitIds, notes: notes || null };
      if (returnType === 'DAMAGED') {
        body.damage = {
          photos: photos.split(',').map(s => s.trim()).filter(Boolean),
          conditionNotes, rootCause, salvageDecision: null,
          costImpact: null, capaId: null,
        };
      }
      const { data } = await api.post(`/v1/projects/${projectId}/mtx/mrns`, body);
      toast.success(`MRN ${data.code} created`);
      onCreated?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'MRN create failed'); }
    setSaving(false);
  };

  const inp = "w-full px-2 py-1.5 rounded border text-xs";
  const lbl = "text-[10px] uppercase font-semibold mb-1 block";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}
      data-testid="mrn-create-dialog">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="p-3 border-b flex items-center justify-between sticky top-0 bg-white" style={{ borderColor: C.border }}>
          <div className="flex items-center gap-2">
            <PackageX className="w-4 h-4" style={{ color: C.accent }} />
            <div>
              <div className="text-sm font-bold" style={{ color: C.slate }}>New Material Return Note</div>
              <div className="text-[9.5px]" style={{ color: C.slateMuted }}>
                Raised by <b>{user.name || user.email || 'current user'}</b> · {new Date().toLocaleString()}
              </div>
            </div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" style={{ color: C.slateMuted }} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className={lbl} style={{ color: C.slateMuted }}>Return Type *</label>
            <div className="grid grid-cols-5 gap-1">
              {RETURN_TYPES.map(t => (
                <button key={t} onClick={() => setReturnType(t)}
                  className="p-1.5 rounded border text-[9px] font-bold"
                  data-testid={`mrn-type-${t}`}
                  style={{
                    borderColor: returnType === t ? C.accent : C.border,
                    background: returnType === t ? '#FEF3C7' : '#fff',
                    color: returnType === t ? '#92400E' : C.slate,
                  }}>{t.replace('_', ' ')}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className={lbl} style={{ color: C.slateMuted }}>Destination Warehouse *</label>
              <select value={warehouseLocation} onChange={e => setWarehouseLocation(e.target.value)}
                className={inp} style={{ borderColor: C.border }} data-testid="mrn-warehouse">
                <option value="">— pick warehouse —</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.location}>
                    {w.label || w.location} · {w.iqcOkStock || 0} IQC-OK
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2 relative">
              <label className={lbl} style={{ color: C.slateMuted }}>Source MIR (search)</label>
              {mirPicked ? (
                <div className="p-1.5 rounded border flex items-center justify-between"
                  style={{ background: '#F0FDF4', borderColor: C.success }} data-testid="mrn-mir-picked">
                  <div className="text-[11px]"><b>{mirPicked.code}</b> · {mirPicked.warehouseLocation} · {mirPicked.status}</div>
                  <button onClick={() => { setMirPicked(null); setMirId(''); setUnitIdsInput(''); }}
                    className="text-[10px]" style={{ color: C.slateMuted }}>change</button>
                </div>
              ) : (
                <input value={mirSearch} onChange={e => setMirSearch(e.target.value)}
                  placeholder="MIR code / workfront / WBS…"
                  className={inp} style={{ borderColor: C.border }} data-testid="mrn-mir-search" />
              )}
              {mirResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded shadow-lg z-30 max-h-56 overflow-y-auto"
                  style={{ borderColor: C.border }}>
                  {mirResults.map((m, i) => (
                    <button key={i} onClick={() => pickMir(m)}
                      className="block w-full text-left px-2 py-1 text-[10px] hover:bg-slate-50"
                      style={{ borderBottom: `1px solid ${C.border}` }}
                      data-testid={`mrn-mir-option-${i}`}>
                      <div className="font-mono font-bold" style={{ color: C.primary }}>{m.code}</div>
                      <div style={{ color: C.slate }}>{m.warehouseLocation} · {m.workfront || 'no workfront'}</div>
                      <div style={{ color: C.slateMuted }}>{m.status} · {(m.reservedUnitIds || []).length} units</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <label className={lbl} style={{ color: C.slateMuted }}>Unit IDs (comma-sep · auto-filled from MIR)</label>
              <input value={unitIdsInput} onChange={e => setUnitIdsInput(e.target.value)}
                placeholder="MU-XXXX, MU-YYYY" className={inp} style={{ borderColor: C.border }}
                data-testid="mrn-units" />
            </div>
            <div className="col-span-2">
              <label className={lbl} style={{ color: C.slateMuted }}>Notes</label>
              <input value={notes} onChange={e => setNotes(e.target.value)}
                className={inp} style={{ borderColor: C.border }} />
            </div>
          </div>
          {returnType === 'DAMAGED' && (
            <div className="p-2 rounded border" style={{ borderColor: C.danger, background: '#FEE2E2' }}>
              <div className="flex items-center gap-1 mb-1.5">
                <AlertTriangle className="w-3.5 h-3.5" style={{ color: C.danger }} />
                <div className="text-[10px] uppercase font-bold" style={{ color: C.danger }}>Damage Details (required)</div>
              </div>
              <label className={lbl} style={{ color: C.slateMuted }}>Photo URLs (comma-sep) *</label>
              <input value={photos} onChange={e => setPhotos(e.target.value)}
                placeholder="https://…, https://…" className={inp} style={{ borderColor: C.border }}
                data-testid="mrn-photos" />
              <div className="flex gap-1.5 mt-1">
                <label className="text-[9px] px-2 py-1 rounded font-semibold cursor-pointer flex items-center gap-1"
                  style={{ background: '#DBEAFE', color: '#1E3A8A', border: `1px solid ${C.border}` }}
                  data-testid="mrn-photo-upload">
                  <Camera className="w-3 h-3" /> Upload
                  <input type="file" accept="image/*" multiple className="hidden"
                    onChange={async e => {
                      const files = Array.from(e.target.files || []);
                      if (files.length === 0) return;
                      const t = toast.loading(`Uploading ${files.length} photo(s)…`);
                      try {
                        const uploaded = await Promise.all(files.map(async f => {
                          const fd = new FormData();
                          fd.append('file', f);
                          fd.append('kind', 'mrn-damage');
                          const { data } = await api.post(
                            `/v1/projects/${projectId}/mtx/uploads/photo`, fd,
                            { headers: { 'Content-Type': 'multipart/form-data' } });
                          return data.url;
                        }));
                        const combined = [photos, ...uploaded].filter(Boolean).join(', ');
                        setPhotos(combined);
                        toast.success(`${uploaded.length} photo(s) attached`, { id: t });
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Upload failed', { id: t });
                      }
                      // reset the input so re-selecting the same file works
                      e.target.value = '';
                    }} />
                </label>
                <label className="text-[9px] px-2 py-1 rounded font-semibold cursor-pointer flex items-center gap-1"
                  style={{ background: '#FEF3C7', color: '#92400E', border: `1px solid ${C.border}` }}
                  data-testid="mrn-photo-camera">
                  <Camera className="w-3 h-3" /> Capture
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={async e => {
                      const f = e.target.files?.[0]; if (!f) return;
                      const t = toast.loading('Uploading photo…');
                      try {
                        const fd = new FormData();
                        fd.append('file', f);
                        fd.append('kind', 'mrn-damage');
                        const { data } = await api.post(
                          `/v1/projects/${projectId}/mtx/uploads/photo`, fd,
                          { headers: { 'Content-Type': 'multipart/form-data' } });
                        setPhotos(photos ? photos + ', ' + data.url : data.url);
                        toast.success('Photo captured', { id: t });
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Upload failed', { id: t });
                      }
                      e.target.value = '';
                    }} />
                </label>
              </div>
              {photos && (
                <div className="flex gap-1 flex-wrap mt-1.5" data-testid="mrn-photo-preview">
                  {photos.split(',').map(u => u.trim()).filter(Boolean).map((u, i) => (
                    <div key={i} className="relative w-12 h-12 rounded border overflow-hidden group"
                      style={{ borderColor: C.border, background: '#F1F5F9' }}>
                      <img src={u} alt={`photo ${i + 1}`} className="w-full h-full object-cover"
                        onError={e => { e.target.style.display = 'none'; }} />
                      <button
                        type="button"
                        onClick={() => {
                          const remaining = photos.split(',').map(s => s.trim()).filter((_, idx) => idx !== i);
                          setPhotos(remaining.join(', '));
                        }}
                        className="absolute top-0 right-0 w-3.5 h-3.5 rounded-bl bg-black/60 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center"
                        title="Remove"
                        data-testid={`mrn-photo-remove-${i}`}>
                        <X className="w-2 h-2" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className={lbl + " mt-2"} style={{ color: C.slateMuted }}>Condition Notes</label>
              <input value={conditionNotes} onChange={e => setConditionNotes(e.target.value)}
                className={inp} style={{ borderColor: C.border }} />
              <label className={lbl + " mt-2"} style={{ color: C.slateMuted }}>Root Cause</label>
              <select value={rootCause} onChange={e => setRootCause(e.target.value)}
                className={inp} style={{ borderColor: C.border }} data-testid="mrn-rootcause">
                <option value="">— pick root cause —</option>
                <option>Handling</option><option>Preservation</option>
                <option>Transit</option><option>Mfg defect</option><option>Other</option>
              </select>
            </div>
          )}
        </div>
        <div className="p-3 border-t flex justify-end gap-2 sticky bottom-0 bg-white" style={{ borderColor: C.border }}>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={saving}
            className="text-white font-semibold" style={{ background: C.accent }}
            data-testid="mrn-create-submit">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
            Create MRN
          </Button>
        </div>
      </div>
    </div>
  );
}

function DamageAssessmentPanel({ projectId, mrn, onClose, onSaved }) {
  const d = mrn.damage || {};
  const [salvage, setSalvage] = useState(d.salvageDecision || '');
  const [cost, setCost] = useState(d.costImpact || '');
  const [capa, setCapa] = useState(d.capaId || '');
  const [rootCause, setRootCause] = useState(d.rootCause || '');
  const [conditionNotes, setConditionNotes] = useState(d.conditionNotes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!salvage) return toast.error('Salvage decision required');
    setSaving(true);
    try {
      await api.patch(`/v1/projects/${projectId}/mtx/mrns/${mrn.id}/damage`, {
        photos: d.photos || [], conditionNotes, rootCause,
        salvageDecision: salvage, costImpact: cost ? parseFloat(cost) : null,
        capaId: capa || null,
      });
      toast.success('Damage Assessment saved');
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}
      data-testid="damage-assessment-panel">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: C.border }}>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" style={{ color: C.danger }} />
            <div className="text-sm font-bold" style={{ color: C.slate }}>Damage Assessment · {mrn.code}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" style={{ color: C.slateMuted }} /></button>
        </div>
        <div className="p-4 space-y-3">
          {(d.photos || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Photos</div>
              <div className="flex gap-1 flex-wrap">
                {(d.photos || []).map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer"
                    className="inline-block w-14 h-14 rounded border overflow-hidden"
                    style={{ borderColor: C.border, background: '#F1F5F9' }}>
                    <img src={u} alt={`damage ${i + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Salvage Decision *</div>
            <div className="grid grid-cols-3 gap-1">
              {SALVAGE.map(s => (
                <button key={s} onClick={() => setSalvage(s)}
                  className="p-1.5 rounded border text-[10px] font-bold"
                  data-testid={`salvage-${s}`}
                  style={{
                    borderColor: salvage === s ? C.accent : C.border,
                    background: salvage === s ? '#FEF3C7' : '#fff',
                    color: salvage === s ? '#92400E' : C.slate,
                  }}>{s.replace('_', ' ')}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Cost Impact</div>
              <input type="number" value={cost} onChange={e => setCost(e.target.value)}
                placeholder="0.00" className="w-full px-2 py-1.5 rounded border text-xs"
                style={{ borderColor: C.border }} data-testid="damage-cost" />
            </div>
            <div>
              <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>CAPA ID</div>
              <input value={capa} onChange={e => setCapa(e.target.value)}
                placeholder="CAPA-2026-…" className="w-full px-2 py-1.5 rounded border text-xs"
                style={{ borderColor: C.border }} data-testid="damage-capa" />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Root Cause</div>
            <input value={rootCause} onChange={e => setRootCause(e.target.value)}
              className="w-full px-2 py-1.5 rounded border text-xs" style={{ borderColor: C.border }} />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Condition Notes</div>
            <textarea value={conditionNotes} onChange={e => setConditionNotes(e.target.value)}
              rows={2} className="w-full px-2 py-1.5 rounded border text-xs" style={{ borderColor: C.border }} />
          </div>
        </div>
        <div className="p-3 border-t flex justify-end gap-2" style={{ borderColor: C.border }}>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving}
            className="text-white font-semibold" style={{ background: C.accent }}
            data-testid="damage-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
            Save Assessment
          </Button>
        </div>
      </div>
    </div>
  );
}

function MrnCard({ mrn, projectId, onApprove, onStatus, onAssess }) {
  const [printGp, setPrintGp] = useState(null);
  const nextStatus = {
    APPROVED: 'PICKED_UP', PICKED_UP: 'AT_SITE_EXIT', AT_SITE_EXIT: 'IN_TRANSIT',
    IN_TRANSIT: 'AT_WH_ENTRY', AT_WH_ENTRY: 'RECEIVED', RECEIVED: 'RECONCILED',
  };
  const next = nextStatus[mrn.status];
  const damaged = mrn.returnType === 'DAMAGED';
  const salvage = mrn.damage?.salvageDecision;
  const openPrint = async () => {
    if (!mrn.gatePassId) return;
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/mtx/gate-passes/${mrn.gatePassId}`);
      setPrintGp(data);
    } catch { toast.error('Return Gate Pass not found'); }
  };
  return (
    <div className="p-2 rounded border mb-1.5 bg-white" style={{ borderColor: C.border }}
      data-testid={`mrn-card-${mrn.code}`}>
      <div className="flex items-center gap-1">
        <div className="text-[10px] font-mono font-bold" style={{ color: C.primary }}>{mrn.code}</div>
        <span className="ml-auto text-[8.5px] font-bold px-1 rounded"
          style={{ background: damaged ? '#FEE2E2' : '#DBEAFE', color: damaged ? '#991B1B' : '#1E3A8A' }}>
          {mrn.returnType?.replace('_', ' ')}
        </span>
      </div>
      <div className="text-[9px] mt-0.5" style={{ color: C.slateMuted }}>
        {(mrn.materialUnitIds || []).length} unit(s) · {mrn.warehouseLocation}
      </div>
      {damaged && !salvage && (
        <div className="mt-1 text-[9px] px-1 rounded inline-block" style={{ background: '#FEE2E2', color: '#991B1B' }}>
          Damage Assessment pending
        </div>
      )}
      {damaged && salvage && (
        <div className="mt-1 text-[9px] px-1 rounded inline-block" style={{ background: '#D1FAE5', color: '#065F46' }}>
          {salvage.replace('_', ' ')}
        </div>
      )}
      {mrn.sourceMir && (
        <div className="mt-1 p-1 rounded text-[8.5px]" style={{ background: '#EEF2FF', color: C.primary }}
          data-testid={`mrn-mir-link-${mrn.code}`}>
          <b>← {mrn.sourceMir.code}</b>
          {mrn.sourceMir.workfront && <> · {mrn.sourceMir.workfront}</>}
          {mrn.sourceMir.wbsActivityId && <> · WBS {mrn.sourceMir.wbsActivityId}</>}
        </div>
      )}
      {(mrn.units || []).length > 0 && (
        <div className="mt-0.5 text-[8.5px]" style={{ color: C.slateMuted }}>
          {mrn.units.slice(0, 2).map(u => (
            <div key={u.id}>
              <b style={{ color: C.slate }}>{u.mciLineNumber}</b> · PO {u.poNumber}/L{u.poLineItem || '—'} · GRN {u.receiptNumber}
            </div>
          ))}
          {mrn.units.length > 2 && <div>+{mrn.units.length - 2} more</div>}
        </div>
      )}
      {mrn.gatePassId && (
        <div className="mt-0.5 text-[8.5px] font-mono" style={{ color: C.accent }}>
          RGP: {mrn.gatePassId}
        </div>
      )}
      <div className="flex gap-1 mt-1.5 flex-wrap">
        {mrn.status === 'DRAFTED' && (
          <button onClick={() => onApprove(mrn.id)}
            className="text-[9px] px-2 py-0.5 rounded text-white font-semibold"
            style={{ background: C.success }} data-testid={`mrn-approve-${mrn.code}`}>
            <Check className="w-2.5 h-2.5 inline mr-0.5" /> Approve
          </button>
        )}
        {mrn.gatePassId && (
          <button onClick={openPrint}
            className="text-[9px] px-2 py-0.5 rounded font-semibold"
            style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' }}
            data-testid={`mrn-print-rgp-${mrn.code}`}
            title="Print A5 Return Gate Pass (QR + RFID barcodes)">
            <Printer className="w-2.5 h-2.5 inline mr-0.5" /> Print RGP
          </button>
        )}
        {damaged && (
          <button onClick={() => onAssess(mrn)}
            className="text-[9px] px-2 py-0.5 rounded"
            style={{ background: '#FEE2E2', color: '#991B1B' }}
            data-testid={`mrn-assess-${mrn.code}`}>
            <Camera className="w-2.5 h-2.5 inline mr-0.5" /> Damage
          </button>
        )}
        {next && (
          <button onClick={() => onStatus(mrn.id, next)}
            className="text-[9px] px-2 py-0.5 rounded"
            style={{ background: '#EEF2FF', color: C.primary }}
            data-testid={`mrn-advance-${mrn.code}`}>
            <ChevronRight className="w-2.5 h-2.5 inline" /> {next.replace(/_/g, ' ')}
          </button>
        )}
      </div>
      {printGp && <GatePassLabel projectId={projectId} gatePass={printGp}
        mir={{ code: mrn.sourceMir?.code || mrn.mirId, workfront: mrn.sourceMir?.workfront,
                wbsActivityId: mrn.sourceMir?.wbsActivityId,
                warehouseLocation: mrn.warehouseLocation,
                lineItems: (mrn.units || []).map(u => ({ mciLineNumber: u.mciLineNumber,
                                                          qty: 1, binLocation: '—' })) }}
        onClose={() => setPrintGp(null)} />}
    </div>
  );
}

function CognaraPanel({ projectId, refreshKey }) {
  const [ins, setIns] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/field-return/cognara-insights`);
        if (alive) setIns(data.insights || []);
      } catch { /* silent */ }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, refreshKey]);
  const sev = { HIGH: C.danger, MEDIUM: C.warn, INFO: C.info };
  return (
    <div className="sticky top-4 w-64 flex-shrink-0 rounded-lg border p-2"
      style={{ borderColor: C.border, background: 'linear-gradient(180deg, #FEF3C7 0%, #FFFFFF 100%)' }}
      data-testid="cognara-panel">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="w-3.5 h-3.5" style={{ color: C.accent }} />
        <div className="text-[10px] uppercase font-bold" style={{ color: C.accent }}>Cognara · Return Flow</div>
      </div>
      {loading && <div className="text-[10px] text-center py-3" style={{ color: C.slateMuted }}>Analyzing…</div>}
      {!loading && ins.map((i, idx) => (
        <div key={idx} className="mb-2 p-2 rounded border bg-white" style={{ borderColor: C.border }}
          data-testid={`cognara-insight-${idx}`}>
          <div className="flex items-center gap-1 mb-1">
            <span className="px-1 rounded text-[8.5px] font-bold text-white" style={{ background: sev[i.severity] || C.slateMuted }}>{i.severity}</span>
            <div className="text-[10px] font-bold" style={{ color: C.slate }}>{i.title}</div>
          </div>
          <div className="text-[9px]" style={{ color: C.slateMuted }}>{i.detail}</div>
          <div className="text-[9px] mt-1 italic" style={{ color: C.primary }}>→ {i.action}</div>
        </div>
      ))}
    </div>
  );
}

export default function FieldReturnTab({ projectId }) {
  const [data, setData] = useState({ buckets: {}, mrns: [] });
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [assessMrn, setAssessMrn] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [autoPrintRgp, setAutoPrintRgp] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [m, k] = await Promise.all([
        api.get(`/v1/projects/${projectId}/mtx/mrns`),
        api.get(`/v1/projects/${projectId}/mtx/field-return/kpis`),
      ]);
      setData(m.data); setKpis(k.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      const { data: r } = await api.patch(`/v1/projects/${projectId}/mtx/mrns/${id}/approve`);
      toast.success(`Approved · Return GP ${r.gatePass.code} · ${r.unitCount} units`);
      load(); setRefreshKey(k => k + 1);
      // Auto-print the Return Gate Pass so security has the physical pass ready
      const mrn = (data.mrns || []).find(m => m.id === id);
      setAutoPrintRgp({
        gatePass: r.gatePass,
        mir: {
          code: mrn?.sourceMir?.code || mrn?.mirId,
          workfront: mrn?.sourceMir?.workfront,
          wbsActivityId: mrn?.sourceMir?.wbsActivityId,
          warehouseLocation: mrn?.warehouseLocation,
          lineItems: (mrn?.units || []).map(u => ({ mciLineNumber: u.mciLineNumber,
                                                     qty: 1, binLocation: '—' })),
        },
      });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
  };

  const advance = async (id, status) => {
    try {
      await api.patch(`/v1/projects/${projectId}/mtx/mrns/${id}/status`, { status });
      toast.success(`Moved to ${status.replace(/_/g, ' ')}`);
      load(); setRefreshKey(k => k + 1);
    } catch { toast.error('Status update failed'); }
  };

  return (
    <div className="p-3 flex gap-3" data-testid="field-return-tab">
      <div className="flex-1 min-w-0 space-y-3">
        {/* 6 KPI tiles */}
        <div className="grid grid-cols-6 gap-2">
          {[
            { lbl: 'Open MRNs',       val: kpis.openMrns || 0,       color: C.primary, id: 'fr-kpi-open' },
            { lbl: 'Awaiting Pickup', val: kpis.awaitingPickup || 0, color: C.warn,    id: 'fr-kpi-pickup' },
            { lbl: 'At Site Exit',    val: kpis.atSiteExit || 0,     color: C.accent,  id: 'fr-kpi-siteexit' },
            { lbl: 'At WH Entry',     val: kpis.atWhEntry || 0,      color: '#F97316', id: 'fr-kpi-whentry' },
            { lbl: 'Received Today',  val: kpis.receivedToday || 0,  color: C.success, id: 'fr-kpi-received' },
            { lbl: 'Damage Rate 30d', val: `${kpis.damageRate30d || 0}%`, color: C.danger, id: 'fr-kpi-damage' },
          ].map(k => (
            <div key={k.id} className="p-2 rounded border bg-white" style={{ borderColor: C.border }}
              data-testid={k.id}>
              <div className="text-[9px] uppercase" style={{ color: C.slateMuted }}>{k.lbl}</div>
              <div className="text-lg font-bold font-mono" style={{ color: k.color }}>{k.val}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowCreate(true)}
            className="text-white font-semibold" style={{ background: C.accent }}
            data-testid="mrn-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New MRN
          </Button>
          <Button size="sm" onClick={() => setScanOpen(true)}
            className="text-white font-semibold" style={{ background: C.primary }}
            data-testid="fr-scan-center-btn">
            <ScanLine className="w-3.5 h-3.5 mr-1" /> Scan Center
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { load(); setRefreshKey(k => k + 1); }}
            data-testid="fr-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <div className="text-[10px]" style={{ color: C.slateMuted }}>
            Approving auto-generates a <b>Return Gate Pass</b> · Damaged MRNs need Damage Assessment before RECEIVED posts to ledger
          </div>
        </div>

        {loading && <div className="p-6 text-center text-sm" style={{ color: C.slateMuted }}>Loading MRNs…</div>}

        {!loading && (
          <div className="grid grid-cols-8 gap-1.5" style={{ minHeight: 240 }} data-testid="mrn-kanban">
            {BUCKETS.map(b => {
              const items = data.buckets?.[b.key] || [];
              return (
                <div key={b.key} className="rounded border" style={{ borderColor: C.border, background: '#FAFAFA' }}
                  data-testid={`mrn-lane-${b.key}`}>
                  <div className="px-1.5 py-1 border-b flex items-center justify-between" style={{ borderColor: C.border }}>
                    <div className="text-[9px] uppercase font-bold truncate" style={{ color: b.color }}>{b.label}</div>
                    <span className="text-[9px] font-mono px-1 rounded" style={{ background: `${b.color}22`, color: b.color }}>{items.length}</span>
                  </div>
                  <div className="p-1 max-h-96 overflow-y-auto">
                    {items.length === 0 && <div className="text-[9px] text-center py-3" style={{ color: C.slateMuted }}>—</div>}
                    {items.map(m => <MrnCard key={m.id} mrn={m} projectId={projectId} onApprove={approve} onStatus={advance} onAssess={setAssessMrn} />)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CognaraPanel projectId={projectId} refreshKey={refreshKey} />

      {showCreate && <CreateMrnDialog projectId={projectId}
        onClose={() => setShowCreate(false)} onCreated={() => { load(); setRefreshKey(k => k + 1); }} />}
      {scanOpen && <ScanCenterDialog projectId={projectId}
        onClose={() => setScanOpen(false)}
        onScanned={() => { load(); setRefreshKey(k => k + 1); }} />}
      {assessMrn && <DamageAssessmentPanel projectId={projectId} mrn={assessMrn}
        onClose={() => setAssessMrn(null)}
        onSaved={() => { load(); setRefreshKey(k => k + 1); }} />}
      {autoPrintRgp && <GatePassLabel projectId={projectId}
        gatePass={autoPrintRgp.gatePass} mir={autoPrintRgp.mir}
        onClose={() => setAutoPrintRgp(null)} />}
    </div>
  );
}
